import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  User,
  Edit3,
  Save,
  X,
  Loader2,
  Key,
  Eye,
  EyeOff,
  Menu,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authService, UpdateProfileDto } from '../services/auth';
import { profileService, ChangePasswordDto } from '../services/profileService';
import AdminSidebar from './AdminSidebar';

const UserProfile: React.FC = () => {
  const { user, setUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [editingPassword, setEditingPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [formData, setFormData] = useState<UpdateProfileDto>({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
  });

  const [passwordData, setPasswordData] = useState<ChangePasswordDto>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleChange =
    (field: keyof UpdateProfileDto) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData({
        ...formData,
        [field]: event.target.value,
      });
    };

  const handlePasswordChange =
    (field: keyof ChangePasswordDto) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPasswordData({
        ...passwordData,
        [field]: event.target.value,
      });
    };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const updatedUser = await authService.updateProfile(formData);
      setUser(updatedUser);
      setEditing(false);
      setSuccess('Profile updated successfully!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSave = async () => {
    try {
      setPasswordLoading(true);
      setPasswordError(null);
      setPasswordSuccess(null);

      if (!passwordData.currentPassword) {
        setPasswordError('Current password is required');
        return;
      }

      if (!passwordData.newPassword) {
        setPasswordError('New password is required');
        return;
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setPasswordError('New password and confirmation do not match');
        return;
      }

      await profileService.changePassword(passwordData);
      setEditingPassword(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setPasswordSuccess('Password changed successfully!');
    } catch (err: any) {
      setPasswordError(
        err.response?.data?.message || 'Failed to change password'
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
    });
    setEditing(false);
    setError(null);
    setSuccess(null);
  };

  const handlePasswordCancel = () => {
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setEditingPassword(false);
    setPasswordError(null);
    setPasswordSuccess(null);
  };

  if (!user) {
    return (
      <div className='flex justify-center items-center min-h-screen'>
        <Loader2 className='h-8 w-8 animate-spin' />
      </div>
    );
  }

  const getBadgeColor = (
    status: string,
    type: 'role' | 'status' | 'verified'
  ) => {
    if (type === 'role') {
      return status === 'admin'
        ? 'bg-blue-100 text-blue-800'
        : 'bg-gray-100 text-gray-800';
    }
    if (type === 'status') {
      return status === 'approved'
        ? 'bg-green-100 text-green-800'
        : 'bg-yellow-100 text-yellow-800';
    }
    return 'bg-green-100 text-green-800';
  };

  return (
    <div className='flex min-h-screen bg-gray-50'>
      {/* Mobile menu button */}
      <div className='lg:hidden fixed top-4 left-4 z-50'>
        <Button
          variant='outline'
          size='sm'
          onClick={() => setSidebarOpen(true)}
          className='bg-white shadow-md'
        >
          <Menu className='h-4 w-4' />
        </Button>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className='lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40'
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <AdminSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main content */}
      <main className='flex-1 lg:ml-60 p-4 sm:p-6 pt-16 lg:pt-6'>
        <div className='space-y-4 sm:space-y-6'>
          {/* Header section */}
          <div className='flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4'>
            <div>
              <h1 className='text-2xl sm:text-3xl font-bold text-gray-900'>
                User Profile
              </h1>
              <p className='text-sm sm:text-base text-gray-600'>
                Manage your account settings and preferences
              </p>
            </div>
          </div>

          {/* Error and Success Messages */}
          {error && (
            <div className='alert-enhanced alert-error'>
              <div className='flex items-center gap-2'>
                <span className='text-red-500'>⚠️</span>
                <span>{error}</span>
              </div>
            </div>
          )}

          {success && (
            <div className='alert-enhanced alert-success'>
              <div className='flex items-center gap-2'>
                <span className='text-green-500'>✅</span>
                <span>{success}</span>
              </div>
            </div>
          )}

          {passwordError && (
            <div className='alert-enhanced alert-error'>
              <div className='flex items-center gap-2'>
                <span className='text-red-500'>⚠️</span>
                <span>{passwordError}</span>
              </div>
            </div>
          )}

          {passwordSuccess && (
            <div className='alert-enhanced alert-success'>
              <div className='flex items-center gap-2'>
                <span className='text-green-500'>✅</span>
                <span>{passwordSuccess}</span>
              </div>
            </div>
          )}

          <Card className='card-enhanced shadow-lg border-0'>
            <CardHeader className='pb-6'>
              <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6'>
                <div className='flex items-start space-x-4 w-full'>
                  <div className='w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-inner'>
                    <User className='w-10 h-10 text-gray-600' />
                  </div>
                  <div className='min-w-0 flex-1'>
                    <CardTitle className='text-2xl font-bold text-gray-900 mb-2'>
                      {user.firstName || ''} {user.lastName || ''}
                    </CardTitle>
                    <p className='text-gray-600 mb-3 break-all'>{user.email}</p>
                    <div className='flex flex-wrap gap-2'>
                      <span
                        className={`px-3 py-1.5 text-sm font-medium rounded-full shadow-sm ${getBadgeColor(user.role, 'role')}`}
                      >
                        {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                      </span>
                      {user.externalUserId && (
                        <span className='px-3 py-1.5 text-sm font-medium rounded-full shadow-sm bg-blue-100 text-blue-800'>
                          Synced
                        </span>
                      )}
                      {user.emailVerified && (
                        <span
                          className={`px-3 py-1.5 text-sm font-medium rounded-full shadow-sm ${getBadgeColor('verified', 'verified')}`}
                        >
                          Email Verified ✓
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className='flex flex-col sm:flex-row gap-2 w-full sm:w-auto mt-4 sm:mt-0'>
                  {!editing ? (
                    <Button
                      variant='outline'
                      onClick={() => setEditing(true)}
                      className='flex items-center gap-2 hover-lift focus-ring transition-smooth'
                    >
                      <Edit3 className='w-4 h-4' />
                      <span className='hidden sm:inline'>Edit Profile</span>
                      <span className='sm:hidden'>Edit</span>
                    </Button>
                  ) : (
                    <div className='flex flex-col sm:flex-row gap-2'>
                      <Button
                        onClick={handleSave}
                        disabled={loading}
                        className={`flex items-center gap-2 btn-enhanced hover-lift focus-ring ${loading ? 'btn-loading' : ''}`}
                      >
                        {loading ? (
                          <Loader2 className='w-4 h-4 animate-spin' />
                        ) : (
                          <Save className='w-4 h-4' />
                        )}
                        Save Changes
                      </Button>
                      <Button
                        variant='outline'
                        onClick={handleCancel}
                        disabled={loading}
                        className='flex items-center gap-2 hover-lift focus-ring transition-smooth'
                      >
                        <X className='w-4 h-4' />
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardHeader>

            <CardContent className='pt-6'>
              <div className='border-t border-gray-100 pt-8'>
                <h3 className='text-lg font-semibold text-gray-900 mb-6'>
                  Personal Information
                </h3>
                <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                  <div className='form-field'>
                    <Label htmlFor='firstName' className='form-label'>
                      First Name
                    </Label>
                    <Input
                      id='firstName'
                      value={formData.firstName}
                      onChange={handleChange('firstName')}
                      disabled={!editing}
                      className={`form-input transition-smooth ${!editing ? 'bg-gray-50 cursor-not-allowed' : 'focus-ring'}`}
                    />
                  </div>

                  <div className='form-field'>
                    <Label htmlFor='lastName' className='form-label'>
                      Last Name
                    </Label>
                    <Input
                      id='lastName'
                      value={formData.lastName}
                      onChange={handleChange('lastName')}
                      disabled={!editing}
                      className={`form-input transition-smooth ${!editing ? 'bg-gray-50 cursor-not-allowed' : 'focus-ring'}`}
                    />
                  </div>

                  <div className='form-field lg:col-span-2'>
                    <Label htmlFor='email' className='form-label'>
                      Email Address
                    </Label>
                    <Input
                      id='email'
                      type='email'
                      value={formData.email}
                      onChange={handleChange('email')}
                      disabled={!editing}
                      className={`form-input transition-smooth ${!editing ? 'bg-gray-50 cursor-not-allowed' : 'focus-ring'}`}
                    />
                  </div>
                </div>
              </div>

              {/* Password Change Section */}
              <div className='border-t border-gray-100 pt-8 mt-8'>
                <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6'>
                  <div>
                    <h3 className='text-lg font-semibold text-gray-900'>
                      Password & Security
                    </h3>
                    <p className='text-sm text-gray-600'>
                      Update your password to keep your account secure
                    </p>
                  </div>
                  {!editingPassword ? (
                    <Button
                      variant='outline'
                      onClick={() => setEditingPassword(true)}
                      className='flex items-center gap-2 hover-lift focus-ring transition-smooth'
                    >
                      <Key className='w-4 h-4' />
                      Change Password
                    </Button>
                  ) : (
                    <div className='flex flex-col sm:flex-row gap-2'>
                      <Button
                        onClick={handlePasswordSave}
                        disabled={passwordLoading}
                        className={`flex items-center gap-2 btn-enhanced hover-lift focus-ring ${passwordLoading ? 'btn-loading' : ''}`}
                      >
                        {passwordLoading ? (
                          <Loader2 className='w-4 h-4 animate-spin' />
                        ) : (
                          <Save className='w-4 h-4' />
                        )}
                        Update Password
                      </Button>
                      <Button
                        variant='outline'
                        onClick={handlePasswordCancel}
                        disabled={passwordLoading}
                        className='flex items-center gap-2 hover-lift focus-ring transition-smooth'
                      >
                        <X className='w-4 h-4' />
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>

                {editingPassword && (
                  <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                    <div className='form-field lg:col-span-2'>
                      <Label htmlFor='currentPassword' className='form-label'>
                        Current Password
                      </Label>
                      <div className='relative'>
                        <Input
                          id='currentPassword'
                          type={showPasswords.current ? 'text' : 'password'}
                          value={passwordData.currentPassword}
                          onChange={handlePasswordChange('currentPassword')}
                          className='form-input focus-ring pr-10'
                          placeholder='Enter your current password'
                        />
                        <button
                          type='button'
                          onClick={() => togglePasswordVisibility('current')}
                          className='absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700'
                        >
                          {showPasswords.current ? (
                            <EyeOff className='h-4 w-4' />
                          ) : (
                            <Eye className='h-4 w-4' />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className='form-field'>
                      <Label htmlFor='newPassword' className='form-label'>
                        New Password
                      </Label>
                      <div className='relative'>
                        <Input
                          id='newPassword'
                          type={showPasswords.new ? 'text' : 'password'}
                          value={passwordData.newPassword}
                          onChange={handlePasswordChange('newPassword')}
                          className='form-input focus-ring pr-10'
                          placeholder='Enter new password'
                        />
                        <button
                          type='button'
                          onClick={() => togglePasswordVisibility('new')}
                          className='absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700'
                        >
                          {showPasswords.new ? (
                            <EyeOff className='h-4 w-4' />
                          ) : (
                            <Eye className='h-4 w-4' />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className='form-field'>
                      <Label htmlFor='confirmPassword' className='form-label'>
                        Confirm New Password
                      </Label>
                      <div className='relative'>
                        <Input
                          id='confirmPassword'
                          type={showPasswords.confirm ? 'text' : 'password'}
                          value={passwordData.confirmPassword}
                          onChange={handlePasswordChange('confirmPassword')}
                          className='form-input focus-ring pr-10'
                          placeholder='Confirm new password'
                        />
                        <button
                          type='button'
                          onClick={() => togglePasswordVisibility('confirm')}
                          className='absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700'
                        >
                          {showPasswords.confirm ? (
                            <EyeOff className='h-4 w-4' />
                          ) : (
                            <Eye className='h-4 w-4' />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className='lg:col-span-2'>
                      <p className='text-xs text-gray-500'>
                        Password must be at least 8 characters long and contain
                        at least one uppercase letter, one lowercase letter, one
                        number, and one special character.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default UserProfile;
